import { Injectable, inject } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { MessageService } from "primeng/api";
import {
  catchError,
  interval,
  last,
  map,
  mergeMap,
  of,
  switchMap,
  takeWhile,
} from "rxjs";
import { ProjectService } from "../../services/project/project.service";
import { RestService } from "../../services/rest.service";
import { ToasterService } from "../../services/toaster-service";
import { MessageEnum } from "../../utils/message.enum";
import { TestCaseGeneratorActions } from "../actions/testcase-generator.actions";

@Injectable()
export class TestCaseGeneratorEffects {
  private messageService = inject(MessageService);
  constructor(
    private actions$: Actions,
    private restService: RestService,
    private readonly toaster: ToasterService,
    private projectService: ProjectService,
    private store: Store
  ) {}
  testcaseVersionId = "";

  loadJiraStories$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TestCaseGeneratorActions.loadJiraStories),
      mergeMap(({ projectId, jiraConnection }) =>
        this.restService.getJiraUserStories(projectId, jiraConnection).pipe(
          switchMap((response: any) => {
            if (response?.status === 201) {
              const asyncToken = response?.asyncToken;
              console.log("Recommendation ID:", asyncToken);

              return interval(20000).pipe(
                switchMap(() =>
                  this.restService.handleAsyncRequest(asyncToken).pipe(
                    map((statusResponse: any) => {
                      if (statusResponse?.asyncReqStatus === "Success") {
                        return TestCaseGeneratorActions.loadJiraStoriesSuccess({
                          stories: statusResponse?.data?.stories,
                        });
                      }
                      if (statusResponse?.asyncReqStatus === "Failed") {
                        this.toaster.pop({
                          type: "error",
                          text: MessageEnum.LOAD_USER_STORIES_FAILURE,
                        });
                        return TestCaseGeneratorActions.loadJiraStoriesFailure({
                          error: "Async request failed",
                        });
                      }
                      return { type: "[UserStories] Polling In Progress" };
                    })
                  )
                ),
                takeWhile(
                  (action: any) =>
                    action.type !==
                      TestCaseGeneratorActions.loadJiraStoriesSuccess.type &&
                    action.type !==
                      TestCaseGeneratorActions.loadJiraStoriesFailure.type,
                  true
                )
              );
            } else {
              return of(
                TestCaseGeneratorActions.loadJiraStoriesFailure({
                  error: "Initial request failed.",
                })
              );
            }
          }),
          catchError((error) => {
            this.toaster.pop({
              type: "error",
              text: error.message || MessageEnum.LOAD_USER_STORIES_FAILURE,
            });
            return of(
              TestCaseGeneratorActions.loadJiraStoriesFailure({
                error: error.message || "Something went wrong!",
              })
            );
          })
        )
      )
    )
  );

  deleteJiraStories$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TestCaseGeneratorActions.deleteJiraStories),
      mergeMap(({ projectId }) =>
        this.restService.deleteJiraStories(projectId).pipe(
          map(() => {
            this.toaster.pop({
              type: "success",
              text: MessageEnum.STORY_DELETE_SUCCESS,
            });
            return TestCaseGeneratorActions.deleteJiraStoriesSuccess();
          }),
          catchError((error) => {
            this.toaster.pop({
              type: "error",
              text: MessageEnum.STORY_DELETE_FAILURE,
            });
            return of(
              TestCaseGeneratorActions.deleteJiraStoriesFailure({
                error: error.message,
              })
            );
          })
        )
      )
    )
  );

  generateTestCases$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TestCaseGeneratorActions.generateTestCases),
      mergeMap(({ selectedUserStories }) =>
        this.restService.generateTestCase(selectedUserStories).pipe(
          switchMap((response: any) => {
            this.testcaseVersionId = response?.data?.versionId; // Extract versionId from the initial response

            console.log("Version ID:", this.testcaseVersionId);
            this.projectService.setTestCaseGenerateVersionId(
              this.testcaseVersionId
            );

            return interval(5000).pipe(
              switchMap(() =>
                this.restService
                  .getTestCaseByVersionId(
                    selectedUserStories.projectId,
                    this.testcaseVersionId
                  )
                  .pipe(
                    map((statusResponse: any) => {
                      // Assuming statusResponse contains `status` property
                      if (statusResponse?.data?.status === "COMPLETED") {
                        const testCasesWithFeedback =
                          statusResponse?.data?.testCases.map(
                            (testCase: any) => ({
                              ...testCase,
                              feedback: "",
                            })
                          );
                        this.toaster.pop({
                          type: "success",
                          text: MessageEnum.TEST_CASE_GENERATION_SUCCESS,
                        });
                        this.store.dispatch(
                          TestCaseGeneratorActions.generateTestCasesProgressMessages(
                            {
                              message: { message : statusResponse?.data?.message, totalPaths: statusResponse?.data?.totalPaths, processingPaths: statusResponse?.data?.processingPaths }
                            }
                          )
                        );
                        return {
                          success: true,
                          testCases: testCasesWithFeedback,
                        };
                      } else if (
                        statusResponse?.data?.status === "FAILURE" ||
                        statusResponse?.data?.status === "FAILED"
                      ) {
                        let errorMessage = statusResponse?.data?.error
                          ? statusResponse?.data?.error
                          : MessageEnum.TEST_CASE_GENERATION_FAILURE;
                        this.toaster.pop({ type: "error", text: errorMessage });
                        this.store.dispatch(
                          TestCaseGeneratorActions.generateTestCasesProgressMessages(
                            {
                              message: { message : statusResponse?.data?.message, totalPaths: statusResponse?.data?.totalPaths, processingPaths: statusResponse?.data?.processingPaths }
                            }
                          )
                        );
                        return {
                          success: true,
                          testCases: statusResponse?.data?.testCases,
                        };
                      } else {
                        this.store.dispatch(
                          TestCaseGeneratorActions.generateTestCasesProgressMessages(
                            {
                              message: { message : statusResponse?.data?.message, totalPaths: statusResponse?.data?.totalPaths, processingPaths: statusResponse?.data?.processingPaths }
                            }
                          )
                        );
                        return { success: false };
                      }
                      // If status is IN-PROGRESS, continue polling
                    })
                  )
              ),
              takeWhile((result) => !result.success, true), // Continue polling until success
              // takeUntil(this.actions$.pipe(ofType(ROUTER_NAVIGATION))), // stop if route changes
              last(), // Take the last successful response
              map((finalResult) => {
                // Save test cases on success
                return TestCaseGeneratorActions.generateTestCasesSuccess({
                  versionId: this.testcaseVersionId,
                  testCases: finalResult.testCases,
                });
              })
            );
          }),
          catchError((error) => {
            return of(
              TestCaseGeneratorActions.generateTestCasesFailure({
                error: error.message,
              })
            );
          })
        )
      )
    )
  );

  updateTestCase$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TestCaseGeneratorActions.updateTestCase),
      mergeMap(({ testCases, projectId }) => {
        const versionId =
          testCases?.[0]?.versionId || this.testcaseVersionId;
        this.testcaseVersionId = versionId;

        const updatedTestCases = testCases.map((testCase: any) => ({
          ...testCase,
          versionId: testCase.versionId || versionId,
        }));

        return this.restService.updateTestCase(updatedTestCases).pipe(
          switchMap(() =>
            this.restService.getTestCaseByVersionId(projectId, versionId).pipe(
              mergeMap((response: any) => {
                const testCasesWithFeedback = (response?.data?.testCases ?? []).map(
                  (testCase: any) => ({ ...testCase, feedback: "" })
                );
                this.toaster.pop({
                  type: "success",
                  text: MessageEnum.FEEDBACK_SUBMIT_SUCCESS,
                });
                // Refresh both slices: generated-test-cases uses testCases;
                // tcg-test-case-detail accordion uses testCasesByVersionId.
                return [
                  TestCaseGeneratorActions.generateTestCasesSuccess({
                    testCases: testCasesWithFeedback,
                    versionId,
                  }),
                  TestCaseGeneratorActions.loadTestCaseDetailsByVersionIdSuccess({
                    testCases: testCasesWithFeedback,
                  }),
                ];
              })
            )
          ),
          catchError((error) => {
            this.toaster.pop({
              type: "error",
              text: MessageEnum.FEEDBACK_SUBMIT_FAILURE,
            });
            return of(
              TestCaseGeneratorActions.updateTestCaseFailure({
                error: error.message,
              })
            );
          })
        );
      })
    )
  );

  loadTestCaseVersions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TestCaseGeneratorActions.loadTestCaseVersions),
      mergeMap(({ projectId }) =>
        this.restService.getTestCaseVersions(projectId).pipe(
          map((versions: any) => {
            return TestCaseGeneratorActions.loadTestCaseVersionsSuccess({
              versions: versions?.data,
            });
          }),
          catchError((error) => {
            return of(
              TestCaseGeneratorActions.loadTestCaseVersionsFailure({
                error: error.message,
              })
            );
          })
        )
      )
    )
  );

  loadTestCaseDetailsByVersionId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TestCaseGeneratorActions.loadTestCaseDetailsByVersionId),
      mergeMap(({ projectId, versionId }) =>
        this.restService.getTestCaseByVersionId(projectId, versionId).pipe(
          map((response: any) => {
            this.testcaseVersionId = versionId;
            return TestCaseGeneratorActions.loadTestCaseDetailsByVersionIdSuccess(
              {
                testCases: response?.data?.testCases,
              }
            );
          }),
          catchError((error) => {
            return of(
              TestCaseGeneratorActions.loadTestCaseDetailsByVersionIdFailure({
                error: error.message,
              })
            );
          })
        )
      )
    )
  );
}
